import * as React from 'react';
import { getDialogTitle, getDocExtension } from './DocumentCreatorHelper';
import { DocumentType } from '../../../operations/createDocument/DocumentType';
import strings from '../../scope/actions/createDocument/DocumentCreator.resx';
import { AutoKeyFacet } from '../../../models/AutoKeyFacet';
import { asAutoAction } from '../../decorators/asAutoAction';
import { ItemDefaultClickAction } from '../defaultClick/DefaultClickAction';
import { IItemCacheDispatch } from '../../../store/actions/ItemCacheActions';
import { NameDialog, INameDialogProps } from '../../nameDialog/NameDialog';
import { CancelError } from '../../../utilities/async/CancelError';
import { createDocument } from '../../../operations/createDocument';
import { targetWindowControl } from '../../../controls/targetWindowControl';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { ControlHandlerProvider } from '../../decorators/withControlHandler';

const newDocumentKeyFacet = new AutoKeyFacet('newDocument');

function getNextNewDocumentKey(): string {
  return newDocumentKeyFacet.serializeNext();
}

const OpenDocumentAction = asAutoAction()(ItemDefaultClickAction);

export type IDocumentCreatorProps = Pick<INameDialogProps, 'validator'> & {
  itemKey: string;
  documentType: DocumentType;
  itemCacheDispatch: IItemCacheDispatch;
  resolve: () => void;
  reject: (error: Error) => void;
  askForFileName?: boolean;
  templateItemKey?: string;
  openWindow: (props: { url?: string; }) => Window | null;
};

export interface IDocumentCreatorState {
  newDocumentKey: string;
  showDialog?: boolean;
  isItemOpened?: boolean;
}

export class DocumentCreator extends React.Component<IDocumentCreatorProps, IDocumentCreatorState> {
  private targetWindow: Window | null = null;

  constructor(props: IDocumentCreatorProps) {
    super(props);

    this.state = {
      newDocumentKey: getNextNewDocumentKey(),
      showDialog: props.askForFileName
    };
  }

  public componentDidMount(): void {
    if (!this.state.showDialog) {
      this._createDocument(undefined);
    }
  }

  public render(): JSX.Element | null {
    const {
      documentType,
      validator
    } = this.props;
    const {
      showDialog,
      isItemOpened
    } = this.state;

    return (
      showDialog ?
        (
          <NameDialog
            title={ getDialogTitle(documentType, strings) || '' }
            name=''
            placeHolder={ strings.NewNamePlaceholder }
            fileExtensionText={ getDocExtension(documentType) }
            onContinue={ this._onContinue }
            onCancel={ this._onCancel }
            continueText={ strings.CreateCommit }
            validator={ validator }
          />
        ) :
        !isItemOpened ?
          (
            <ControlHandlerProvider
              controlHandlers={
                targetWindowControl.pack(this._targetWindowControlHandler)
              }
            >
              <OpenDocumentAction
                onComplete={ this._onItemOpened }
                itemKey={ this.state.newDocumentKey }
              />
            </ControlHandlerProvider>
          ) :
          null
    );
  }

  private _targetWindowControlHandler = (
    itemCache: IItemCache,
    props: typeof targetWindowControl.inputType
  ): typeof targetWindowControl.returnType => {
    const {
      targetWindow
    } = this;

    if (!props.targetWindow && targetWindow) {
      return {
        targetWindow: targetWindow
      };
    }

    return {};
  }

  private _onItemOpened = (): void => {
    this.setState({
      isItemOpened: true
    });

    this.props.resolve();
  }

  private _onContinue = (options: { name: string; }): Promise<void> => {
    return this._createDocument(options.name).then(() => {
      this.setState({
        showDialog: false
      });
    });
  }

  private _onCancel = (): void => {
    this.props.reject(new CancelError());
  }

  private _createDocument = (fileName: string | undefined): Promise<void> => {
    this.targetWindow = this.props.openWindow({});

    return createDocumentExecutor(this.state, this.props, fileName, this.targetWindow);
  }
}

export function createDocumentExecutor(
  creatorState: IDocumentCreatorState,
  creatorProps: IDocumentCreatorProps,
  fileName: string | undefined,
  targetWindow?: Window | null
): Promise<void> {
  const {
    itemCacheDispatch,
    itemKey,
    documentType,
    askForFileName,
    templateItemKey
  } = creatorProps;

  const {
    newDocumentKey
  } = creatorState;

  if (!askForFileName || (askForFileName && fileName)) {
    return itemCacheDispatch(createDocument({
      items: {
        [itemKey]: {
          documentType: documentType,
          newDocumentKey: newDocumentKey,
          targetWindow: targetWindow,
          fileName: fileName,
          templateItemKey: templateItemKey
        }
      }
    }));
  } else {
    return Promise.resolve();
  }
}
